import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { OdooMigrationForm } from "../components/odoo/OdooMigrationForm";
import { odooService } from "../services/odooService";

/**
 * Pagina de Importacion de Base de Datos Odoo 16
 *
 * REGLAS: Solo <Stack>, size="small", desktop-first, max 500 lineas
 */
export function OdooRemoteSyncPage(): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<any>(null);
  const [backendConnected, setBackendConnected] = useState<boolean | null>(
    null,
  );
  const [showTechnical, setShowTechnical] = useState(false);

  const checkAll = useCallback(async () => {
    try {
      const isConnected = await odooService.checkBackendConnection();
      setBackendConnected(isConnected);
    } catch {
      setBackendConnected(false);
    }
    try {
      const status = await odooService.getMigrationStatus();
      setMigrationStatus(status);
    } catch {
      // silent - backend may be down
    }
  }, []);

  useEffect(() => {
    checkAll();
  }, [checkAll]);

  // Auto-refresh while migration is running
  useEffect(() => {
    if (!migrationStatus?.isRunning) return;
    const interval = setInterval(async () => {
      try {
        const status = await odooService.getMigrationStatus();
        setMigrationStatus(status);
      } catch {
        /* silent */
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [migrationStatus?.isRunning]);

  const handleMigrationSuccess = () => {
    checkAll();
  };

  const handleCancel = async () => {
    try {
      await odooService.cancelMigration();
      checkAll();
    } catch {
      /* silent */
    }
  };

  const envInfo = odooService.getEnvironmentInfo();
  const isRunning = migrationStatus?.isRunning;
  const isComplete = !isRunning && migrationStatus?.progress === 100;

  // Derive status for the indicator dot
  const statusColor =
    backendConnected === false
      ? "error"
      : isRunning
        ? "info"
        : isComplete
          ? "success"
          : "default";
  const statusLabel =
    backendConnected === false
      ? "Backend desconectado"
      : isRunning
        ? "Migracion en progreso"
        : isComplete
          ? "Completada"
          : "Lista";

  return (
    <Stack spacing={3} sx={{ p: 3, maxWidth: 720, width: "100%" }}>
      {/* Header */}
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <StorageIcon sx={{ fontSize: 28, color: "primary.main" }} />
          <Typography variant="h5" component="h1" fontWeight={600}>
            Importar Base de Datos Odoo 16
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ pl: 5.5 }}>
          Importa el respaldo SQL de Odoo 16 (historico) a la base de datos
          local <code>odoo_16</code>.
        </Typography>
      </Stack>

      {/* Connection status - compact */}
      {backendConnected === false && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={checkAll} startIcon={<RefreshIcon />}>
              Reintentar
            </Button>
          }
        >
          Backend no disponible en puerto 3000. Verifica que{" "}
          <code>npm run dev:all</code> este corriendo.
        </Alert>
      )}

      {/* Main action card */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {/* Status bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor:
              statusColor === "error"
                ? "error.50"
                : statusColor === "success"
                  ? "success.50"
                  : statusColor === "info"
                    ? "info.50"
                    : "grey.50",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor:
                  statusColor === "error"
                    ? "error.main"
                    : statusColor === "success"
                      ? "success.main"
                      : statusColor === "info"
                        ? "info.main"
                        : "grey.400",
              }}
            />
            <Typography variant="body2" fontWeight={500}>
              {statusLabel}
            </Typography>
            {isRunning && (
              <Typography variant="body2" color="text.secondary">
                - {Math.round(migrationStatus.progress)}%
              </Typography>
            )}
          </Stack>
          <IconButton size="small" onClick={checkAll} title="Actualizar estado">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Progress bar when running */}
        {isRunning && (
          <LinearProgress
            variant="determinate"
            value={migrationStatus.progress}
            sx={{ height: 3 }}
          />
        )}

        {/* Card body */}
        <Stack spacing={2.5} sx={{ p: 2.5 }}>
          {/* What this does - only show when not running */}
          {!isRunning && !isComplete && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Esta accion importa el archivo <strong>villapps.sql</strong>{" "}
                (~5.6 GB) a la base de datos <code>odoo_16</code> local. Los
                datos historicos de ventas, productos y compras quedan
                disponibles para los calculos de forecast.
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <InfoIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                <Typography variant="caption" color="text.disabled">
                  Archivo:{" "}
                  {envInfo.environment === "development"
                    ? "../BKOdoo/villapps.sql"
                    : "/home/ec2-user/VillApps_MxP/odoo-data/villapps.sql"}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Running state */}
          {isRunning && (
            <Stack spacing={1.5}>
              <Typography variant="body2" fontWeight={500}>
                {migrationStatus.currentStep || "Procesando..."}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                La importacion puede tardar varios minutos dependiendo del
                tamano del archivo. No cierres esta pagina.
              </Typography>
            </Stack>
          )}

          {/* Completed state */}
          {isComplete && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ py: 0.5 }}
            >
              Importacion completada exitosamente. La base de datos{" "}
              <code>odoo_16</code> esta lista.
            </Alert>
          )}

          {/* Action buttons */}
          <Stack direction="row" spacing={1.5}>
            {!isRunning ? (
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={() => setConfirmOpen(true)}
                size="small"
                disabled={backendConnected === false}
              >
                {isComplete
                  ? "Reimportar Base de Datos"
                  : "Importar Base de Datos"}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                size="small"
              >
                Cancelar
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Technical details - collapsible */}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setShowTechnical(!showTechnical)}
          sx={{
            px: 2.5,
            py: 1,
            cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Detalles tecnicos
          </Typography>
          {showTechnical ? (
            <ExpandLessIcon fontSize="small" sx={{ color: "text.disabled" }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: "text.disabled" }} />
          )}
        </Stack>
        <Collapse in={showTechnical}>
          <Stack spacing={1} sx={{ px: 2.5, pb: 2 }}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.disabled">
                  Entorno
                </Typography>
                <Typography variant="body2">{envInfo.info.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.disabled">
                  Base de datos destino
                </Typography>
                <Typography variant="body2">
                  {envInfo.config.targetDatabase}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.disabled">
                  Workers
                </Typography>
                <Typography variant="body2">
                  {envInfo.config.parallelWorkers}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.disabled">
                  Timeout
                </Typography>
                <Typography variant="body2">
                  {envInfo.config.timeoutSeconds}s
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                label="PostgreSQL 16+"
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: 11 }}
              />
              <Chip
                label={envInfo.info.platform}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: 11 }}
              />
              <Chip
                label={`${envInfo.environment === "development" ? "DEV" : "PROD"}`}
                size="small"
                variant="outlined"
                color={
                  envInfo.environment === "development" ? "warning" : "success"
                }
                sx={{ height: 22, fontSize: 11 }}
              />
            </Stack>
          </Stack>
        </Collapse>
      </Paper>

      {/* Confirmation dialog */}
      <OdooMigrationForm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onSuccess={handleMigrationSuccess}
      />
    </Stack>
  );
}
